<template>
	<div class="delivery__city-input">
		<div class="city__label">Населений пункт</div>
		<v-select
			v-model="selectedCity"
			:options="cityOptions"
			label="label"
			:filterable="false"
			:clearable="false"
			@search="onSearch"
			@search:focus="onSearchFocus"
			@option:selected="onSelect"
			placeholder="Обери населений пункт"
		>
			<template #no-options="{ searching, search }">
				<span v-if="searching && search">Вибачте, немає відповідних варіантів</span>
				<span v-else>Почніть вводити назву населеного пункту</span>
			</template>
		</v-select>
	</div>
</template>

<script setup>
// @ts-nocheck
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { DEFAULT_FREQUENT_CITIES } from '../constants/frequentCities';

const props = defineProps({
	modelValue: {
		type: Object,
		default: null
	}
});

const emit = defineEmits(['update:modelValue']);

const cityOptions = ref(Array());
const selectedCity = ref(props.modelValue || null);

let fetchController;
const CITY_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const CITY_CACHE_KEY_PREFIX = 'np_city_cache_v1';

const fallbackFrequentCities = DEFAULT_FREQUENT_CITIES;

const resolveNpConf = () => {
	try {
		if (window['globalConf'] && window['globalNpConf']) {
			return window['globalConf'](window['globalNpConf']);
		}

		if (window['NpConf']) {
			return window['NpConf'];
		}
	} catch (error) {
		console.error('Failed to resolve NP config', error);
	}

	return null;
};
const getFrequentCitiesMap = () => {
	const globalFrequentCities = window['subscription']?.frequentCities;

	if (!globalFrequentCities || typeof globalFrequentCities !== 'object') {
		return fallbackFrequentCities;
	}

	return {
		...fallbackFrequentCities,
		...globalFrequentCities
	};
};

const getFrequentCities = () => {
	return Object.values(getFrequentCitiesMap());
};

const createDropdown = (items = []) => {
	cityOptions.value = Array.isArray(items) ? items : [];
};

const buildCacheKey = (shopName = '', term = '') => {
	return `${CITY_CACHE_KEY_PREFIX}:${shopName}:${term.toLowerCase()}`;
};

const readCityCache = (cacheKey = '') => {
	try {
		const raw = localStorage.getItem(cacheKey);
		if (!raw) {
			return null;
		}

		const payload = JSON.parse(raw);
		if (!payload?.expiresAt || payload.expiresAt <= Date.now() || !Array.isArray(payload.data)) {
			localStorage.removeItem(cacheKey);
			return null;
		}

		return payload.data;
	} catch (error) {
		localStorage.removeItem(cacheKey);
		return null;
	}
};
const writeCityCache = (cacheKey = '', data = []) => {
	try {
		const payload = {
			expiresAt: Date.now() + CITY_CACHE_TTL_MS,
			data
		};
		localStorage.setItem(cacheKey, JSON.stringify(payload));
	} catch (error) {
		console.error('Failed to write city cache', error);
	}
};
const fetchCities = async (term = '') => {
	const npConf = resolveNpConf();
	const endpoint = npConf?.url?.getNpCity;
	const shopName = npConf?.shop_name;

	if (!endpoint || !shopName) {
		createDropdown([]);
		return;
	}

	const cacheKey = buildCacheKey(shopName, term);
	const cached = readCityCache(cacheKey);
	if (cached) {
		createDropdown(cached);
		return;
	}

	if (fetchController) {
		fetchController.abort();
	}

	fetchController = new AbortController();

	try {
		const response = await fetch(
			`${endpoint}?query=${encodeURIComponent(term)}&shop_name=${encodeURIComponent(shopName)}`,
			{ signal: fetchController.signal }
		);
		const data = await response.json();
		writeCityCache(cacheKey, data);
		createDropdown(data);
	} catch (error) {
		if (!(error instanceof DOMException && error.name === 'AbortError')) {
			console.error('Failed to fetch cities', error);
			createDropdown([]);
		}
	}
};
const onSearch = (query = '') => {
	const term = (query || '').trim();

	if (term.length <= 1) {
		createDropdown(getFrequentCities());
		return;
	}

	if (term.length >= 3) {
		fetchCities(term);
		return;
	}

	createDropdown([]);
};
const toggleQuickCities = (selectedLabel = '') => {
	const quickCityElements = document.querySelectorAll('.quick-cities span, .delivery__list button');
	const frequentCitiesMap = getFrequentCitiesMap();

	quickCityElements.forEach((element) => {
		const cityName = element.textContent?.trim();
		const cityData = cityName ? frequentCitiesMap[cityName] : null;
		const isMatch = cityData && cityData.label === selectedLabel;
		element.classList.toggle('active', Boolean(isMatch));
	});
};
const onSelect = (item = null) => {
	if (!item || !item.label) {
		return;
	}

	selectedCity.value = item;
	emit('update:modelValue', item);

	sessionStorage.setItem('city_id', item.city_id || '');
	sessionStorage.setItem('delivery_city_id', item.delivery_city_id || '');
	sessionStorage.setItem('city_label', item.label || '');
	sessionStorage.setItem('city_name', item.meta?.MainDescription || '');
	sessionStorage.setItem('np_region', item.meta?.Region || '');
	sessionStorage.setItem('np_area', item.meta?.Area || '');

	sessionStorage.setItem('department_label', '');
	sessionStorage.setItem('department_number', '');

	const warehouseInput = document.querySelector('.np_warehouse');
	if (warehouseInput instanceof HTMLInputElement) {
		warehouseInput.value = '';
	}

	toggleQuickCities(item.label);
};

const onSearchFocus = () => {
	const hasValue = selectedCity.value?.label;
	if (!hasValue) {
		createDropdown(getFrequentCities());
	}
};

watch(() => props.modelValue, (value) => {
	selectedCity.value = value || null;
});

onMounted(() => {
	const storedLabel = sessionStorage.getItem('city_label');
	if (!props.modelValue && storedLabel) {
		selectedCity.value = { label: storedLabel };
		emit('update:modelValue', selectedCity.value);
	}

	createDropdown(getFrequentCities());
});

onBeforeUnmount(() => {
	if (fetchController) {
		fetchController.abort();
	}
});
</script>
